/**
 * License validation service for paper compliance checking.
 * Implements business logic for license compliance rules and validation.
 */

// Note: toast will be imported from the UI component that uses this service
import { 
  ArxivLicense, 
  LicenseValidationResult,
  LicenseValidationError,
  isNonCompliantLicense 
} from '@/types/license';
import { fetchArxivLicenseFromUrl, fetchArxivLicense } from '@/lib/api/arxiv-license';

/**
 * Validates license compliance according to platform policies.
 * 
 * Current policy: Reject CC BY-NC-ND licenses (non-commercial, no derivatives)
 * as they are incompatible with academic sharing and derivative work.
 * 
 * @param license License information to validate
 * @returns Whether the license is compliant with platform policies
 */
export function validateLicenseCompliance(license: ArxivLicense): boolean {
  // Check for non-compliant license patterns
  return !isNonCompliantLicense(license);
}

/**
 * Comprehensive license validation that checks both format and compliance.
 * 
 * @param license License object to validate
 * @returns Detailed validation result with error messages
 */
export function validateLicense(license: ArxivLicense): LicenseValidationResult {
  const errors: string[] = [];
  let isValid = true;

  // Basic structure validation
  if (!license.id || typeof license.id !== 'string') {
    errors.push('License must have a valid ID');
    isValid = false;
  }

  if (!license.name || typeof license.name !== 'string') {
    errors.push('License must have a valid name');
    isValid = false;
  }

  if (typeof license.isCompliant !== 'boolean') {
    errors.push('License must have a valid compliance status');
    isValid = false;
  }

  if (!Array.isArray(license.restrictions)) {
    errors.push('License must have a valid restrictions array');
    isValid = false;
  }

  // If basic validation fails, return early
  if (!isValid) {
    return {
      isValid: false,
      isCompliant: false,
      errors
    };
  }

  // Check compliance
  const isCompliant = validateLicenseCompliance(license);
  if (!isCompliant) {
    errors.push(`License "${license.name}" is not compliant with platform policies (no derivatives and non-commercial restrictions are not permitted)`);
  }

  // Additional validation for specific license types
  if (license.url && !isValidLicenseUrl(license.url)) {
    errors.push('License URL format is invalid');
    isValid = false;
  }

  return {
    isValid: isValid && isCompliant,
    isCompliant,
    license: isValid ? license : undefined,
    errors
  };
}

/**
 * Validates license URL format
 */
function isValidLicenseUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    // Allow Creative Commons, arXiv, and other common license domains
    const allowedDomains = [
      'creativecommons.org',
      'arxiv.org', 
      'opensource.org',
      'gnu.org'
    ];
    
    return allowedDomains.some(domain => urlObj.hostname.includes(domain));
  } catch {
    return false;
  }
}

/**
 * Fetches and validates license for an arXiv paper URL.
 * This is the main validation entry point for the upload workflow.
 * 
 * @param arxivUrl ArXiv paper URL
 * @returns Promise resolving to validation result
 */
export async function validateArxivPaperLicense(arxivUrl: string): Promise<LicenseValidationResult> {
  try {
    // Fetch license information from arXiv
    const fetchResult = await fetchArxivLicenseFromUrl(arxivUrl);
    
    if (!fetchResult.success || !fetchResult.license) {
      return {
        isValid: false,
        isCompliant: false,
        errors: [fetchResult.error || 'Failed to fetch license information']
      };
    }

    // Validate the fetched license
    const validationResult = validateLicense(fetchResult.license);
    
    // Add user-friendly error messages for common issues
    if (!validationResult.isCompliant) {
      const friendlyErrors = validationResult.errors.map(error => {
        if (error.includes('not compliant')) {
          return `This paper uses a ${fetchResult.license.name} license, which restricts commercial use and derivative works. Papers with these restrictions cannot be uploaded to maintain open academic sharing.`;
        }
        return error;
      });
      
      return {
        ...validationResult,
        errors: friendlyErrors
      };
    }

    return validationResult;

  } catch (error) {
    console.error('License validation error:', error);
    
    const errorMessage = error instanceof LicenseValidationError 
      ? error.message 
      : 'An unexpected error occurred during license validation';
    
    return {
      isValid: false,
      isCompliant: false,
      errors: [errorMessage]
    };
  }
}

/**
 * Batch validates licenses for multiple arXiv papers.
 * Useful for processing existing papers or bulk operations.
 * 
 * @param arxivIds Array of arXiv paper IDs
 * @param progressCallback Optional progress update callback
 * @returns Promise resolving to array of validation results
 */
export async function batchValidateArxivLicenses(
  arxivIds: string[],
  progressCallback?: (completed: number, total: number, currentId?: string) => void
): Promise<{ arxivId: string; result: LicenseValidationResult }[]> {
  const results = [];
  
  for (let i = 0; i < arxivIds.length; i++) {
    const arxivId = arxivIds[i];
    
    if (progressCallback) {
      progressCallback(i, arxivIds.length, arxivId);
    }
    
    try {
      // Fetch license for this paper
      const fetchResult = await fetchArxivLicense(arxivId);
      
      let validationResult: LicenseValidationResult;
      
      if (!fetchResult.success || !fetchResult.license) {
        validationResult = {
          isValid: false,
          isCompliant: false,
          errors: [fetchResult.error || 'Failed to fetch license']
        };
      } else {
        validationResult = validateLicense(fetchResult.license);
      }
      
      results.push({
        arxivId,
        result: validationResult
      });
      
    } catch (error) {
      console.error(`Batch validation failed for ${arxivId}:`, error);
      
      results.push({
        arxivId,
        result: {
          isValid: false,
          isCompliant: false,
          errors: [`Batch validation error: ${error instanceof Error ? error.message : 'Unknown error'}`]
        }
      });
    }
  }
  
  if (progressCallback) {
    progressCallback(arxivIds.length, arxivIds.length);
  }
  
  return results;
}

/**
 * Validates license before paper creation with optional user feedback.
 * Integrates with the upload workflow to provide immediate feedback.
 * 
 * @param arxivUrl ArXiv paper URL
 * @param showToasts Optional function to show user notifications
 * @returns Promise resolving to validation result
 */
export async function validateLicenseForUpload(
  arxivUrl: string,
  showToasts?: {
    error: (title: string, description: string) => void;
    success: (title: string, description: string) => void;
  }
): Promise<LicenseValidationResult> {
  try {
    const result = await validateArxivPaperLicense(arxivUrl);
    
    // Show user feedback if toast functions provided
    if (showToasts) {
      if (!result.isValid) {
        // Show error toast for invalid or non-compliant licenses
        const primaryError = result.errors[0] || 'License validation failed';
        showToasts.error('License Validation Failed', primaryError);
      } else if (result.license) {
        // Show success toast with license information
        showToasts.success(
          'License Validated', 
          `Paper uses ${result.license.name} - Compatible with platform policies`
        );
      }
    }
    
    return result;
    
  } catch (error) {
    console.error('Upload license validation error:', error);
    
    if (showToasts) {
      showToasts.error(
        'License Check Failed', 
        'Could not validate paper license. Please try again or contact support.'
      );
    }
    
    return {
      isValid: false,
      isCompliant: false,
      errors: ['License validation service unavailable']
    };
  }
}

/**
 * Gets user-friendly description of license restrictions
 * @param license License information
 * @returns Array of user-friendly restriction descriptions
 */
export function getLicenseRestrictionDescriptions(license: ArxivLicense): string[] {
  const descriptions = [];
  
  for (const restriction of license.restrictions) {
    switch (restriction.type) {
      case 'non-commercial':
        descriptions.push('🚫 Commercial use not permitted');
        break;
      case 'no-derivatives':
        descriptions.push('🚫 Modifications not permitted');
        break;
      case 'share-alike':
        descriptions.push('📋 Derivative works must use same license');
        break;
      default:
        descriptions.push(`ℹ️ ${restriction.description}`);
    }
  }
  
  return descriptions;
}

/**
 * Gets compliance summary for admin/monitoring purposes
 * @param validationResults Array of validation results
 * @returns Compliance statistics
 */
export function getComplianceSummary(validationResults: LicenseValidationResult[]) {
  const total = validationResults.length;
  const compliant = validationResults.filter(r => r.isCompliant).length;
  const nonCompliant = validationResults.filter(r => !r.isCompliant && r.isValid).length;
  const invalid = validationResults.filter(r => !r.isValid).length;
  
  return {
    total,
    compliant,
    nonCompliant,
    invalid,
    complianceRate: total > 0 ? (compliant / total) * 100 : 0
  };
}